/**
 * Catalogue des membres (RG-20 à RG-23) : tous les livres en mémoire depuis
 * Firestore (cache local persistant), index MiniSearch, filtres et tri.
 */
import { defineStore } from 'pinia'
import { computed, ref, shallowRef, watch } from 'vue'
import type MiniSearch from 'minisearch'
import type { Unsubscribe } from 'firebase/firestore'
import { subscribeBooks } from '@/services/books'
import { normalize } from '@/services/format'
import { buildIndex, searchIds } from '@/services/search'
import type { Book } from '@/types'

export type Availability = 'all' | 'available' | 'borrowed'
export type SortKey = 'title' | 'author' | 'added'
export type ViewMode = 'grid' | 'list' | 'shelves'

const VIEW_KEY = 'exlibris.viewMode'

function loadViewMode(): ViewMode {
  try {
    const v = localStorage.getItem(VIEW_KEY)
    if (v === 'grid' || v === 'list' || v === 'shelves') return v
  } catch {
    // Stockage indisponible (navigation privée) : valeur par défaut.
  }
  return 'grid'
}

const collator = new Intl.Collator('fr', { sensitivity: 'base', numeric: true })

export interface Shelf {
  name: string
  books: Book[]
}

export const useCatalogStore = defineStore('catalog', () => {
  const books = shallowRef<Book[]>([])
  const loaded = ref(false)
  const error = ref<string | null>(null)
  let unsub: Unsubscribe | null = null

  // Filtres et présentation (RG-22).
  const query = ref('')
  const availability = ref<Availability>('all')
  const author = ref('')
  const location = ref('')
  const sort = ref<SortKey>('added')
  const viewMode = ref<ViewMode>(loadViewMode())
  watch(viewMode, (v) => {
    try {
      localStorage.setItem(VIEW_KEY, v)
    } catch {
      // Sans effet si le stockage est indisponible.
    }
  })

  const index = shallowRef<MiniSearch<Book> | null>(null)
  const byId = computed(() => new Map(books.value.map((b) => [b.id, b])))

  function start() {
    if (unsub) return
    unsub = subscribeBooks(
      (list) => {
        books.value = list
        index.value = buildIndex(list, ['title', 'authors', 'personalNotes'])
        loaded.value = true
        error.value = null
      },
      () => {
        error.value = 'Le catalogue n’a pas pu être chargé. Vérifiez votre connexion, puis rechargez la page.'
        loaded.value = true
      },
    )
  }

  function stop() {
    unsub?.()
    unsub = null
    books.value = []
    index.value = null
    loaded.value = false
  }

  // Listes pour les filtres et les suggestions d'emplacement (RG-44).
  const locations = computed(() => {
    const counts = new Map<string, number>()
    for (const b of books.value) if (b.location) counts.set(b.location, (counts.get(b.location) ?? 0) + 1)
    return [...counts.entries()].map(([name, count]) => ({ name, count })).sort((a, b) => collator.compare(a.name, b.name))
  })

  const authors = computed(() => {
    const seen = new Map<string, string>()
    for (const b of books.value) for (const a of b.authors) if (!seen.has(normalize(a))) seen.set(normalize(a), a)
    return [...seen.values()].sort(collator.compare)
  })

  const stats = computed(() => {
    const borrowed = books.value.filter((b) => b.loan.status === 'borrowed').length
    return {
      total: books.value.length,
      authors: authors.value.length,
      locations: locations.value.length,
      borrowed,
      available: books.value.length - borrowed,
    }
  })

  function compare(a: Book, b: Book): number {
    switch (sort.value) {
      case 'title':
        return collator.compare(a.title, b.title)
      case 'author':
        return collator.compare(a.authors[0] ?? '', b.authors[0] ?? '') || collator.compare(a.title, b.title)
      case 'added':
        return b.createdAt.getTime() - a.createdAt.getTime()
    }
  }

  /** Livres filtrés puis triés. Avec une recherche, ordre de pertinence. */
  const filtered = computed<Book[]>(() => {
    let list: Book[]
    const q = query.value.trim()
    if (q !== '' && index.value) {
      const map = byId.value
      list = searchIds(index.value, q)
        .map((id) => map.get(id))
        .filter((b): b is Book => b !== undefined)
    } else {
      list = [...books.value].sort(compare)
    }
    const authorKey = normalize(author.value)
    return list.filter(
      (b) =>
        (availability.value === 'all' || b.loan.status === availability.value) &&
        (location.value === '' || b.location === location.value) &&
        (authorKey === '' || b.authors.some((a) => normalize(a) === authorKey)),
    )
  })

  /** Vue étagères (1c) : une section par emplacement. */
  const shelves = computed<Shelf[]>(() => {
    const groups = new Map<string, Book[]>()
    for (const b of filtered.value) {
      const key = b.location || 'Sans emplacement'
      const g = groups.get(key)
      if (g) g.push(b)
      else groups.set(key, [b])
    }
    return [...groups.entries()]
      .map(([name, list]) => ({ name, books: list }))
      .sort((a, b) => collator.compare(a.name, b.name))
  })

  const hasFilters = computed(
    () => query.value !== '' || availability.value !== 'all' || author.value !== '' || location.value !== '',
  )

  function resetFilters() {
    query.value = ''
    availability.value = 'all'
    author.value = ''
    location.value = ''
  }

  /** Livres empruntés par un utilisateur (RG-35). */
  function borrowedBy(uid: string): Book[] {
    return books.value
      .filter((b) => b.loan.status === 'borrowed' && b.loan.borrowerUid === uid)
      .sort((a, b) => (a.loan.borrowedAt?.getTime() ?? 0) - (b.loan.borrowedAt?.getTime() ?? 0))
  }

  /** Prêts en cours, du plus ancien au plus récent (RG-36). */
  const currentLoans = computed(() =>
    books.value
      .filter((b) => b.loan.status === 'borrowed')
      .sort((a, b) => (a.loan.borrowedAt?.getTime() ?? 0) - (b.loan.borrowedAt?.getTime() ?? 0)),
  )

  return {
    books,
    loaded,
    error,
    query,
    availability,
    author,
    location,
    sort,
    viewMode,
    byId,
    locations,
    authors,
    stats,
    filtered,
    shelves,
    hasFilters,
    currentLoans,
    start,
    stop,
    resetFilters,
    borrowedBy,
  }
})
